import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, Optional } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { sortBy } from 'lodash';
import { map, first } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { ProjectsService } from '../projects.service';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FwAvatarModule, FwButtonModule, FwButtonToggleModule, FwCardModule, FwContainedInputModule, FwIconButtonModule, FwIconModule, FwLayoutsModule, FwMenuModule, FwSectionHeadingModule, FwSelectMenuModule, FwSwitchModule, FwTextInputModule } from '@flywheel-io/vision';
import { AdminModule } from 'app/src/admin/admin.module';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { NotificationService } from '../../notification.service';
import { ProjectsQueryService } from '../projects-query.service';
import { PermissionService } from '../../permission.service';

interface DialogData {
  groupId?: string,
  isAdmin?: boolean
}

@Component({
  selector: 'create-project',
  styleUrls: ['./create-project-dialog.component.scss'],
  templateUrl: './create-project-dialog.component.html',
  imports: [
    AdminModule,
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwAvatarModule,
    FwButtonModule,
    FwButtonToggleModule,
    FwCardModule,
    FwContainedInputModule,
    FwIconButtonModule,
    FwIconModule,
    FwLayoutsModule,
    FwMenuModule,
    FwSectionHeadingModule,
    FwSelectMenuModule,
    FwSwitchModule,
    FwTextInputModule,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    ScrollingModule,
    SessionModule,
    SharedModule,
    RouterModule,
  ],
})
export class CreateProjectDialog {
  projectsLabel = new FormControl('', Validators.required);
  groupPicker = new FormControl(null, Validators.required);
  groupsWithAccess$ = this.projectsService.getGroupsWithAccess(ContainerAccess.Admin, this.data?.isAdmin).pipe(
    map(groups => sortBy(groups, group => (group.label || group._id).toLowerCase())),
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) @Optional() private data: DialogData,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
    private projectsQueryService: ProjectsQueryService,
    private permissionQuerySerivce: PermissionService,
    private ref: MatDialogRef<CreateProjectDialog>,
  ) {
    this.ref.disableClose = false;
    this.groupPicker.valueChanges.subscribe(() => {
      // manually reset the duplicateLabel error from the API
      if (this.projectsLabel.hasError('duplicateLabel')) {
        this.projectsLabel.setErrors(null);
      }
    });

    this.groupsWithAccess$.pipe(first()).subscribe((groups) => {
      // only assign the group from context if we have permission to add projects to it
      if (this.data && this.data.groupId) {
        const group = groups.find(g => g._id === this.data.groupId);
        if (group) {
          this.groupPicker.setValue(group._id);
        }
      }
    });
  }

  submit(): void {
    this.coreApi.projects.post({ label: this.projectsLabel.value, group: this.groupPicker.value }, { inherit: true })
      .subscribe({
        next: (project) => {
          this.notifications.success('projectAdmin.createNew.success');
          this.projectsService.getProjects({ refresh: true });
          this.projectsQueryService.invalidateProjectListCache();
          this.permissionQuerySerivce.invalidatePermissionsCache();
          this.ref.close(project);
        },
        error: (error: HttpErrorResponse) => {
          this.notifications.error('projectAdmin.createNew.error');
          if (error.status === 409) {
            this.projectsLabel.setErrors({
              duplicateLabel: true,
            });
          }
          this.logger.error(error);
        },
      });
  }

}
